import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { Loading } from '@/components/Loading';
import { Menu } from '@/components/Menu';
import { Modal } from '@/components/Modal';
import { EventDetailsCard } from '@/partials/dashboard/Events/EventDetails/EventDetailsCard';
import { EventDetailsInformations } from '@/partials/dashboard/Events/EventDetails/EventDetailsInformations';
import { EventDetailsMeetingRequests } from '@/partials/dashboard/Events/EventDetails/EventDetailsMeetingRequests';
import { EventDetailsUsers } from '@/partials/dashboard/Events/EventDetails/EventDetailsUsers';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEventStore } from '@/stores/useEventStore';
import { cn } from '@/utils/cn';
import { handleError } from '@/utils/handleError';
import { motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCalendar } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const EventDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { event, showEvent, addEventUser, removeEventUser } = useEventStore();
  const [activeSection, setActiveSection] = useState(0);
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (id) {
      showEvent(parseInt(id));
    }
  }, [id, showEvent]);

  // Ecouter le changement d'inscription d'un utilisateur
  useEffect(() => {
    if (
      !event?.user_is_subscribed &&
      searchParams.get('section') === '1' &&
      activeSection === 1
    ) {
      setActiveSection(0);
      // Changer le paametre d'url
      setSearchParams(prev => {
        prev.set('section', '0');
        return prev;
      });
    }
  }, [event?.user_is_subscribed, searchParams, setSearchParams, activeSection]);

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleSubscribe = async () => {
    try {
      if (event?.user_is_subscribed) {
        await removeEventUser(event.id, user?.id as number);
        navigate(`/${i18n.language}/dashboard/events`);
      } else {
        await addEventUser(event?.id as number, user?.id as number);
      }
      handleClose();
    } catch (error) {
      handleError(error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!event) {
    return <Loading />;
  }

  const sections = [
    {
      key: <EventDetailsInformations />,
      label: t('eventDetails:informations'),
    },
    {
      key: <EventDetailsCard />,
      label: t('eventDetails:card.title'),
      disabled:
        ((event.status === 'future' || event.status === 'ongoing') &&
          !event.user_is_subscribed) ||
        event.status === 'past',
    },
    {
      key: <EventDetailsUsers />,
      label: t('eventDetails:participants'),
    },
    {
      key: <EventDetailsMeetingRequests />,
      label: t('eventDetails:meetingRequests.title'),
      notificationCount:
        event?.meeting_requests > 0 ? event?.meeting_requests : undefined,
    },
  ];

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour et actions */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              <FaArrowLeft className="size-4" />
              {t('common:back')}
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-lg font-semibold text-secondary">
              {event.name}
            </h1>
          </div>

          <Button
            type="button"
            onClick={handleOpen}
            className={cn(
              event.user_is_subscribed && 'bg-red-500 hover:bg-red-600',
              !event.user_is_subscribed && 'bg-green-500 hover:bg-green-600',
            )}
            disabled={event.status === 'ongoing' || event.status === 'past'}
          >
            {event.user_is_subscribed
              ? t('eventDetails:unsubscribe.button')
              : t('eventDetails:subscribe.button')}
          </Button>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Image de couverture */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-gray-100 shadow-sm">
            {event.cover ? (
              <Image
                src={event.cover}
                alt={event.name}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                <BsCalendar className="size-12 text-primary/50" />
              </div>
            )}
          </div>
        </motion.div>

        {/* Menu de navigation */}
        <motion.div variants={itemVariants} className="mb-6">
          <Menu
            sections={sections}
            onSectionChange={setActiveSection}
            className="overflow-x-auto"
          />
        </motion.div>

        {/* Contenu de la section active */}
        <motion.div
          key={activeSection}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          {sections[activeSection].key}
        </motion.div>
      </motion.div>

      {/* Modal de confirmation */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        className="w-auto"
        title={
          event.user_is_subscribed
            ? t('eventDetails:unsubscribe.title')
            : t('eventDetails:subscribe.title')
        }
      >
        <p className="mb-4 text-gray-600">
          {event.user_is_subscribed
            ? t('eventDetails:unsubscribe.description')
            : t('eventDetails:subscribe.description')}
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            onClick={handleClose}
            className="w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 sm:w-auto"
          >
            {t('common:cancel')}
          </Button>
          <Button
            className={cn(
              'w-full sm:w-auto',
              event.user_is_subscribed
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-green-500 hover:bg-green-600',
            )}
            onClick={handleSubscribe}
          >
            {event.user_is_subscribed
              ? t('eventDetails:unsubscribe.button')
              : t('eventDetails:subscribe.button')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
